import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE from '../config';

export default function EmployeeDocuments() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [docs, setDocs] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [signedFilter, setSignedFilter] = useState('all');

  const lookup = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    setError('');
    setDocs(null);
    setEmployee(null);
    try {
      const res = await fetch(`${API_BASE}/admin/employee-documents/${encodeURIComponent(id)}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Not found');
      setEmployee(data.employee);
      setDocs(data.documents || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const val = search.trim();
    if (!val) return;
    lookup(val);
  };

  const filtered = (docs || []).filter(d => {
    if (typeFilter !== 'all' && d.type !== typeFilter) return false;
    if (signedFilter === 'signed' && !d.signed) return false;
    if (signedFilter === 'unsigned' && d.signed) return false;
    return true;
  });

  const openDoc = async (doc) => {
    if (doc.url) {
      window.open(doc.url, '_blank');
    } else if (doc.downloadUrl) {
      window.open(`${API_BASE}${doc.downloadUrl}`, '_blank');
    }
  };

  return (
    <div style={s.page}>
      <div style={s.container}>
        <button onClick={() => navigate('/admin')} className="btn-back-nav" style={{ marginBottom: 16 }}>← Back</button>
        <h1 style={s.title}>📄 Employee Documents</h1>
        <p style={s.sub}>Search for an employee to view their equipment handover and return protocols</p>

        <form onSubmit={handleSearch} style={s.searchBar}>
          <input
            style={s.searchInput}
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Enter Employee ID or name…"
          />
          <button style={s.btnPrimary} type="submit">Search</button>
        </form>

        {error && <div style={s.alertErr}>{error}</div>}
        {loading && <div style={s.center}>Loading…</div>}

        {employee && (
          <div style={s.empCard}>
            <div style={s.empAvatar}>{(employee.firstName?.[0] || employee.username?.[0] || '?').toUpperCase()}</div>
            <div>
              <h2 style={{ margin: '0 0 4px', color: '#1e293b' }}>
                {employee.firstName} {employee.lastName}
              </h2>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 4 }}>
                <Tag label="ID" value={employee.employeeId} />
                <Tag label="Dept" value={employee.department} />
                {employee.country && <Tag label="Country" value={`${employee.country.name} (${employee.country.code})`} color="#dbeafe" textColor="#1e40af" />}
                {employee.client && <Tag label="Client" value={employee.client.name} color="#ede9fe" textColor="#6d28d9" />}
                <Tag label="Role" value={employee.role} color="#fef3c7" textColor="#92400e" />
              </div>
            </div>
          </div>
        )}

        {docs && (
          <>
            <div style={s.filterBar}>
              <div>
                <label style={s.filterLabel}>Type</label>
                <select style={s.select} value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
                  <option value="all">All</option>
                  <option value="handover">Handover</option>
                  <option value="return">Return</option>
                </select>
              </div>
              <div>
                <label style={s.filterLabel}>Status</label>
                <select style={s.select} value={signedFilter} onChange={e => setSignedFilter(e.target.value)}>
                  <option value="all">All</option>
                  <option value="signed">Signed</option>
                  <option value="unsigned">Unsigned</option>
                </select>
              </div>
              <span style={{ color: '#64748b', fontSize: '0.85rem', alignSelf: 'flex-end' }}>
                {filtered.length} document{filtered.length !== 1 ? 's' : ''}
              </span>
            </div>

            {filtered.length === 0 ? (
              <div style={s.empty}>No documents match the current filters.</div>
            ) : (
              <div style={s.tableWrap}>
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>Document</th>
                      <th style={s.th}>Type</th>
                      <th style={s.th}>Date</th>
                      <th style={s.th}>Status</th>
                      <th style={s.th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((doc, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={s.td}>
                          <div style={{ fontWeight: 600, color: '#1e293b' }}>{doc.label || doc.filename}</div>
                          {doc.equipmentName && <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{doc.equipmentName}</div>}
                        </td>
                        <td style={s.td}>
                          <span style={doc.type === 'handover' ? s.badgeBlue : s.badgePurple}>
                            {doc.type === 'handover' ? '📦 Handover' : '↩️ Return'}
                          </span>
                        </td>
                        <td style={s.td}>
                          {doc.date ? new Date(doc.date).toLocaleDateString() : '—'}
                        </td>
                        <td style={s.td}>
                          {doc.signed
                            ? <span style={s.badgeGreen}>✓ Signed</span>
                            : <span style={s.badgeGray}>Unsigned</span>}
                        </td>
                        <td style={s.td}>
                          {(doc.url || doc.downloadUrl) ? (
                            <button style={s.btnSm} onClick={() => openDoc(doc)}>View / Download</button>
                          ) : (
                            <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>No file</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Tag({ label, value, color = '#f1f5f9', textColor = '#475569' }) {
  if (!value) return null;
  return (
    <span style={{ background: color, color: textColor, borderRadius: 5, padding: '2px 10px', fontSize: '0.8rem', fontWeight: 600 }}>
      {label}: {value}
    </span>
  );
}

const s = {
  page: { minHeight: '100vh', background: '#f8fafc', padding: '32px 16px' },
  container: { maxWidth: 1000, margin: '0 auto' },
  title: { margin: '0 0 4px', fontSize: '1.6rem', color: '#1e293b', fontWeight: 700 },
  sub: { margin: '0 0 24px', color: '#64748b', fontSize: '0.9rem' },
  searchBar: { display: 'flex', gap: 10, marginBottom: 20 },
  searchInput: { flex: 1, padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: '0.95rem' },
  alertErr: { background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '10px 16px', borderRadius: 8, marginBottom: 16 },
  center: { textAlign: 'center', padding: 40, color: '#94a3b8' },
  empCard: { display: 'flex', alignItems: 'flex-start', gap: 16, background: '#fff', borderRadius: 12, padding: 20, marginBottom: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.07)', border: '1px solid #e2e8f0' },
  empAvatar: { width: 48, height: 48, borderRadius: '50%', background: '#2563eb', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem', fontWeight: 700, flexShrink: 0 },
  filterBar: { display: 'flex', gap: 16, alignItems: 'flex-end', marginBottom: 14, flexWrap: 'wrap' },
  filterLabel: { display: 'block', marginBottom: 4, color: '#374151', fontWeight: 600, fontSize: '0.82rem' },
  select: { padding: '8px 12px', borderRadius: 7, border: '1px solid #d1d5db', fontSize: '0.9rem', background: '#fff' },
  tableWrap: { background: '#fff', borderRadius: 10, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.07)', border: '1px solid #e2e8f0' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '11px 16px', background: '#f8fafc', color: '#374151', fontWeight: 700, fontSize: '0.85rem', textAlign: 'left', borderBottom: '1px solid #e2e8f0' },
  td: { padding: '11px 16px', color: '#374151', fontSize: '0.9rem', verticalAlign: 'middle' },
  badgeBlue: { background: '#dbeafe', color: '#1e40af', borderRadius: 5, padding: '2px 9px', fontSize: '0.8rem', fontWeight: 600 },
  badgePurple: { background: '#ede9fe', color: '#6d28d9', borderRadius: 5, padding: '2px 9px', fontSize: '0.8rem', fontWeight: 600 },
  badgeGreen: { background: '#dcfce7', color: '#15803d', borderRadius: 5, padding: '2px 9px', fontSize: '0.8rem', fontWeight: 600 },
  badgeGray: { background: '#f1f5f9', color: '#64748b', borderRadius: 5, padding: '2px 9px', fontSize: '0.8rem', fontWeight: 600 },
  btnPrimary: { background: '#2563eb', color: '#fff', border: 'none', borderRadius: 7, padding: '9px 20px', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem' },
  btnSm: { background: '#f1f5f9', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, padding: '5px 12px', fontSize: '0.8rem', cursor: 'pointer' },
  empty: { background: '#fff', borderRadius: 10, padding: 40, textAlign: 'center', color: '#64748b', border: '1px solid #e2e8f0' }
};
